import React, { useState, useCallback, useEffect } from 'react';
import { Button, Typography, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect from '../../../components/DropdownSelect/DropdownSelect';
import pallet from '../../../layouts/pallet';
import { academicYears } from '../../../constants/academicYears';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType } from '../../../enums';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import Icons from '../../../components/icons';

interface DeregisterEnachDialogProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const DeregisterEnachDialog: React.FC<DeregisterEnachDialogProps> = ({
  open,
  onClose,
  userId,
  onShowToast
}) => {
  const defaultAcademicYear = academicYears.find((year) => year.isDefault);
  const [academicYear, setAcademicYear] = useState<string>(
    defaultAcademicYear?.value || academicYears[0].value
  );
  const [isLoading, setIsLoading] = useState(false);

  const resetState = useCallback(() => {
    setAcademicYear(defaultAcademicYear?.value || academicYears[0].value);
    setIsLoading(false);
  }, [defaultAcademicYear]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const isDeregisterDisabled = !academicYear;

  const handleDeregister = async () => {
    setIsLoading(true);
    if (!userId) {
      onShowToast('User ID is missing.', SnackbarSeverityType.error);
      console.error('User ID is missing.');
      setIsLoading(false);
      return;
    }

    try {
      await paymentSchemesApis.deregisterEnach({ userId, academicYear });
      onShowToast(
        'ENACH deregistered successfully!',
        SnackbarSeverityType.success
      );
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Failed to deregister ENACH:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to deregister ENACH.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleDeregister}
      sx={{ textTransform: 'none' }}
      disabled={isDeregisterDisabled || isLoading}
    >
      Deregister
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Deregister ENACH'
      maxWidth='sm'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 2,
          mb: 2,
          backgroundColor: pallet.lightYellow,
          border: `1px solid ${pallet.borderYellow}`,
          borderRadius: '4px'
        }}
      >
        <Icons.Warning />
        <Typography variant='body2' color={pallet.Y400}>
          <Typography component='span' fontWeight='bold'>
            ** Note
          </Typography>
          <br />
          Deregistering ENACH will stop the auto-debits for all the further
          instalments of the selected Academic Year's Fees.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            value={academicYear}
            options={academicYears}
            onChange={(e) => setAcademicYear(e.target.value as string)}
            name='academicYear'
            fullWidth
            hasTopLabel
          />
        </Box>
        <Box sx={{ flex: 1 }} />
      </Box>
    </DialogModal>
  );
};

export default DeregisterEnachDialog;
